"use client"
import { Box } from "@mui/material"
import { useEffect, useRef, memo } from "react"

const SpaceBackground = memo(() => {
    const canvasRef = useRef(null)

    useEffect(() => {
      const canvas = canvasRef.current
      if (!canvas) return

      const resizeCanvas = () => {
        canvas.width = window.innerWidth
        canvas.height = window.innerHeight
      }

      resizeCanvas()
      window.addEventListener("resize", resizeCanvas)

      const ctx = canvas.getContext("2d")

      class Particle {
        constructor() {
          this.reset()
        }

        reset() {
          this.size = 0.8 + Math.random() * 2
          const edgeStart = Math.random() > 0.3

          if (edgeStart) {
            const edge = Math.floor(Math.random() * 4)
            switch (edge) {
              case 0:
                this.x = Math.random() * canvas.width
                this.y = -5
                break
              case 1:
                this.x = canvas.width + 5
                this.y = Math.random() * canvas.height
                break
              case 2:
                this.x = Math.random() * canvas.width
                this.y = canvas.height + 5
                break
              case 3:
                this.x = -5
                this.y = Math.random() * canvas.height
                break
            }
          } else {
            this.x = Math.random() * canvas.width
            this.y = Math.random() * canvas.height
          }

          if (edgeStart) {
            this.vx = (canvas.width / 2 - this.x) * 0.00005 + (Math.random() * 0.2 - 0.1)
            this.vy = (canvas.height / 2 - this.y) * 0.00005 + (Math.random() * 0.2 - 0.1)
          } else {
            const angle = Math.random() * Math.PI * 2
            const speed = 0.2 + Math.random() * 0.3
            this.vx = Math.cos(angle) * speed
            this.vy = Math.sin(angle) * speed
          }

          this.alpha = 0.2 + Math.random() * 0.6
          this.color = `rgba(0, 229, 255, ${this.alpha})`
          this.glowColor = `rgba(0, 229, 255, ${this.alpha * 0.3})`
        }

        update() {
          this.x += this.vx
          this.y += this.vy
          if (this.x < -10 || this.x > canvas.width + 10 || this.y < -10 || this.y > canvas.height + 10) {
            this.reset()
          }
        }

        draw() {
          ctx.beginPath()
          ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2)
          ctx.fillStyle = this.color
          ctx.fill()

          ctx.beginPath()
          ctx.arc(this.x, this.y, this.size * 2, 0, Math.PI * 2)
          ctx.fillStyle = this.glowColor
          ctx.fill()
        }
      }

      const particles = []
      const particleCount = 25

      for (let i = 0; i < particleCount; i++) {
        particles.push(new Particle())
      }

      let animationFrameId
      let lastFrameTime = 0
      const frameDuration = 1000 / 20

      const animate = (timestamp) => {
        animationFrameId = requestAnimationFrame(animate)
        if (timestamp - lastFrameTime < frameDuration) return
        lastFrameTime = timestamp

        ctx.clearRect(0, 0, canvas.width, canvas.height)
        for (let i = 0; i < particles.length; i++) {
          particles[i].update()
          particles[i].draw()
        }
      }

      animationFrameId = requestAnimationFrame(animate)

      return () => {
        window.removeEventListener("resize", resizeCanvas)
        cancelAnimationFrame(animationFrameId)
      }
    }, [])

    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: -1,
          overflow: "hidden",
          // Base gradient + static stars + nebulas all in one layer (no extra DOM elements)
          background: `
            radial-gradient(ellipse at 80% 20%, rgba(0, 100, 255, 0.1) 0%, transparent 70%),
            radial-gradient(ellipse at 20% 70%, rgba(0, 229, 255, 0.1) 0%, transparent 80%),
            radial-gradient(ellipse at 60% 90%, rgba(100, 0, 255, 0.05) 0%, transparent 70%),
            radial-gradient(circle, rgba(255, 255, 255, 0.8) 0.5px, transparent 0.5px),
            radial-gradient(circle, rgba(255, 255, 255, 0.6) 0.8px, transparent 0.8px),
            radial-gradient(circle, rgba(255, 255, 255, 0.4) 1px, transparent 1px),
            linear-gradient(to bottom, #000000, #050520)
          `,
          backgroundSize: `
            100% 100%,
            100% 100%,
            100% 100%,
            30px 30px,
            80px 80px,
            150px 150px,
            100% 100%
          `,
          backgroundPosition: `
            0 0,
            0 0,
            0 0,
            0 0,
            15px 15px,
            30px 30px,
            0 0
          `,
        }}
      >
        {/* Single animated hexagon grid layer */}
        <Box
          className="hexagon-grid"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.15,
          }}
        />

        {/* Canvas for particles */}
        <canvas
          ref={canvasRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
          }}
        />

        <style>{`
          .hexagon-grid {
            position: relative;
          }

          .hexagon-grid::before {
            content: "";
            position: absolute;
            top: -60px;
            left: -60px;
            right: -60px;
            bottom: -60px;
            background-image:
              linear-gradient(to right, rgba(0, 229, 255, 1) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(0, 229, 255, 1) 1px, transparent 1px),
              repeating-linear-gradient(60deg, rgba(0, 229, 255, 1), rgba(0, 229, 255, 1) 1px, transparent 1px, transparent 60px),
              repeating-linear-gradient(120deg, rgba(0, 229, 255, 1), rgba(0, 229, 255, 1) 1px, transparent 1px, transparent 60px);
            background-size: 60px 60px;
            opacity: 0.1;
            pointer-events: none;
            will-change: transform;
            animation: moveGrid 60s infinite linear;
          }

          @keyframes moveGrid {
            from { transform: translate3d(0, 0, 0); }
            to { transform: translate3d(60px, 60px, 0); }
          }
        `}</style>
      </Box>
    )
  })

  export default SpaceBackground
